import { useCallback, useEffect, useState } from "react";
import { api } from "../api/apiClient";
import type { VacationRequest } from "../api/types";

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleDateString([], {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

/**
 * Pending vacation requests inbox for owners and managers, shown on the
 * Schedule page. Owners see every request; managers see florists at their
 * own shop (server-enforced).
 */
export function VacationApprovalsCard() {
  const [rows, setRows] = useState<VacationRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [noteById, setNoteById] = useState<Record<string, string>>({});

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      setRows(await api.listVacationRequests({ status: "PENDING" }));
      setError(null);
    } catch (err: unknown) {
      setError(
        err instanceof Error ? err.message : "Could not load vacation requests.",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const decide = async (id: string, outcome: "approve" | "reject") => {
    setBusyId(id);
    setError(null);
    try {
      const note = noteById[id]?.trim() || undefined;
      if (outcome === "approve") await api.approveVacationRequest(id, note);
      else await api.rejectVacationRequest(id, note);
      await refresh();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Could not save decision.");
    } finally {
      setBusyId(null);
    }
  };

  if (!loading && rows.length === 0 && !error) return null;

  return (
    <div className="card" style={{ display: "grid", gap: "var(--space-3)" }}>
      <div>
        <strong style={{ fontSize: 16 }}>Vacation requests</strong>
        <p className="muted" style={{ margin: "var(--space-1) 0 0", fontSize: 13 }}>
          Pending approvals. Approved vacations appear on the schedule.
        </p>
      </div>

      {error ? <div className="alert error">{error}</div> : null}

      {loading ? (
        <p className="muted">Loading…</p>
      ) : (
        <table>
          <thead>
            <tr>
              <th>Who</th>
              <th>Dates</th>
              <th>Reason</th>
              <th>Note</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id}>
                <td>
                  {r.user.fullName}
                  <span className="muted" style={{ marginLeft: 6, fontSize: 12 }}>
                    {r.user.role}
                  </span>
                </td>
                <td>
                  {fmtDate(r.startDate)} – {fmtDate(r.endDate)}
                </td>
                <td className="muted">{r.reason ?? "—"}</td>
                <td>
                  <input
                    type="text"
                    placeholder="Optional note"
                    value={noteById[r.id] ?? ""}
                    onChange={(e) =>
                      setNoteById((m) => ({ ...m, [r.id]: e.target.value }))
                    }
                    disabled={busyId === r.id}
                  />
                </td>
                <td>
                  <div style={{ display: "flex", gap: "var(--space-2)" }}>
                    <button
                      type="button"
                      className="button"
                      onClick={() => void decide(r.id, "approve")}
                      disabled={busyId === r.id}
                    >
                      Approve
                    </button>
                    <button
                      type="button"
                      className="button danger"
                      onClick={() => void decide(r.id, "reject")}
                      disabled={busyId === r.id}
                    >
                      Reject
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
